import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Lock, User, ArrowRight } from '@phosphor-icons/react';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [demoAccounts, setDemoAccounts] = useState([]);
  const [demoBusy, setDemoBusy] = useState('');
  const { login, demoLogin } = useAuth();
  const navigate = useNavigate();

  // The server decides whether this deployment offers demo sign-ins at all.
  // A 404 means the feature is off, which is not an error worth showing.
  useEffect(() => {
    let cancelled = false;
    api.get('/auth/demo-accounts')
      .then(({ data }) => { if (!cancelled && Array.isArray(data)) setDemoAccounts(data); })
      .catch(() => { if (!cancelled) setDemoAccounts([]); });
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
      navigate('/');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(d => d.msg || JSON.stringify(d)).join(' '));
      else setError('Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async (account) => {
    setError('');
    setDemoBusy(account.username);
    try {
      await demoLogin(account.username);
      navigate('/');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : `Could not sign in as ${account.full_name}.`);
    } finally {
      setDemoBusy('');
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left panel - image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1571176242081-bf093bb3a10d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmUlMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc3NjI1OTYwNXww&ixlib=rb-4.1.0&q=85"
          alt="Office"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <h1 className="font-heading text-4xl font-bold text-white tracking-tight">
            Request for Payment System
          </h1>
          <p className="mt-3 text-white/80 text-sm max-w-md leading-relaxed">
            Centralized payment request management, approval workflow, and financial analytics.
          </p>
        </div>
      </div>

      {/* Right panel - login form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm py-8">
          <div className="mb-8">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-[#0F172A]">
              Sign In
            </h2>
            <p className="mt-1 text-sm text-[#475569]">
              Enter your credentials to access the system
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-sm bg-[#FEE2E2] text-[#991B1B] text-sm border border-[#EF4444]/30" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="username" className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">
                Username
              </Label>
              <div className="relative mt-1">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                  className="pl-9 rounded-sm h-10"
                  required
                  data-testid="login-username-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">
                Password
              </Label>
              <div className="relative mt-1">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="pl-9 rounded-sm h-10"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-sm h-10 bg-[#0055FF] hover:bg-[#0043CC] text-white font-medium"
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {demoAccounts.length > 0 && (
            <div className="mt-8" data-testid="demo-logins">
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-[#E2E8F0]" />
                <span className="text-[10px] uppercase tracking-[0.1em] font-medium text-[#94A3B8]">
                  Demo sign-in
                </span>
                <div className="h-px flex-1 bg-[#E2E8F0]" />
              </div>

              <p className="mt-3 text-xs text-[#475569] leading-relaxed">
                One click signs you in as that role, no password. Follow a request down the
                chain by switching roles.
              </p>

              <div className="mt-3 space-y-1.5">
                {demoAccounts.map((account) => (
                  <button
                    key={account.username}
                    type="button"
                    onClick={() => handleDemoLogin(account)}
                    disabled={!!demoBusy}
                    className="group w-full flex items-center gap-3 text-left rounded-sm border border-[#E2E8F0] px-3 py-2 transition-colors hover:border-[#0055FF] hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={`demo-login-${account.username}`}
                  >
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-[#0F172A] truncate">
                        {account.full_name}
                      </span>
                      <span className="block text-xs text-[#475569] leading-snug">
                        {demoBusy === account.username ? 'Signing in...' : account.description}
                      </span>
                    </span>
                    <ArrowRight
                      size={14}
                      className="shrink-0 text-[#94A3B8] transition-colors group-hover:text-[#0055FF]"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
